import { Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { FilterPanelGroup, FilterPanelToggle } from '../../../../components/filters/filter-panel-group';
import { FilterPanelHeader } from '../../../../components/filters/filter-panel-header';
import { FilterPanelToggleSlider } from '../../../../components/filters/filter-panel-toggle-slider';
import { SmallSquareButton } from '../../../../components/small-square-button/small-square-button';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  resetFilters,
  setCommittedNextWeeksEnabled,
  setCommittedNextWeeksValue,
  setOnlyBookmarkedFilter,
  toggleCollapsedGroup,
} from '../../../../store/strategy-store/strategy-store.actions';
import { Divider } from '../../../../ui-library/divider/divider';
import { Text } from '../../../../ui-library/typography/typography';
import { useFilterGroups } from './filters.hooks';
import type { FilterGroup } from './filters.type';

export function Filters({ setShow, title }: { setShow: (show: boolean) => void; title?: string }) {
  const i18n = useTranslation();
  const filterGroups = useFilterGroups();
  const collapsedGroups = useStrategyStore((state) => state.collapsedGroups);
  const onlyBookmarkedFilter = useStrategyStore((state) => state.filters.only_bookmarked || false);
  const enabledCommittedNextWeeksFilter = useFeatureFlag(FeatureFlag.InitiativeCommittedFilter);
  const committedNextWeeksEnabled = useStrategyStore((state) => state.filters.committed_next_weeks_enabled || false);
  const committedNextWeeksValue = useStrategyStore((state) => state.filters.committed_next_weeks_value || 4);

  const isGroupCollapsed = (groupId: string): boolean => {
    return collapsedGroups[groupId];
  };

  const isAllSelected = (group: FilterGroup): boolean => {
    return (
      (group.selectedValues || []).length > 0 && (group.selectedValues || []).length === (group.options || []).length
    );
  };

  const handleAllChange = (group: FilterGroup, isChecked: boolean): void => {
    group.onSelectionChange(isChecked ? group.options.map((opt) => opt.value) : []);
  };

  const handleIndividualChange = (group: FilterGroup, value: string, isChecked: boolean): void => {
    const newValues = isChecked ? [...group.selectedValues, value] : group.selectedValues.filter((v) => v !== value);

    group.onSelectionChange(newValues);
  };

  return (
    <Stack data-bf="strategy-filter-initiatives" gap="md" h="100%">
      <Group justify="space-between">
        <Text size="medium" weight="medium">
          {title || i18n.t('strategy.initiatives.initiatives_title')}
        </Text>
        <SmallSquareButton
          data-bf="strategy-filter-btn-close"
          onClick={() => setShow(false)}
          iconName="arrow_back_ios"
        />
      </Group>
      <Divider />
      <FilterPanelHeader onResetFilters={resetFilters} />
      <Divider />

      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack gap="md">
          <FilterPanelToggle
            toggle={{
              id: 'only_bookmarked',
              name: i18n.t('strategy.filters.bookmarked_only'),
              icon: 'bookmark_added',
              selectedValue: onlyBookmarkedFilter,
              onSelectionChange: setOnlyBookmarkedFilter,
            }}
            hasDivider
          />
          {enabledCommittedNextWeeksFilter && (
            <FilterPanelToggleSlider
              toggle={{
                id: 'committed_next_weeks',
                icon: 'calendar_month',
                name: i18n.t('strategy.filters.committed_next_weeks'),
                tooltip:
                  'Filter initiatives by their committed date. Shows initiatives with end dates in the next N weeks.',
                toggleValue: committedNextWeeksEnabled,
                onToggleChange: setCommittedNextWeeksEnabled,
                sliderValue: committedNextWeeksValue,
                onSliderChange: setCommittedNextWeeksValue,
                sliderMin: 1,
                sliderMax: 12,
                sliderStep: 1,
                sliderLabel: (value) => `Next ${value} ${value === 1 ? 'week' : 'weeks'}`,
              }}
              hasDivider
            />
          )}
          {filterGroups.map((group) => (
            <FilterPanelGroup
              key={group.id}
              group={group}
              toggleCollapsed={(group) => toggleCollapsedGroup(group.id)}
              isCollapsed={isGroupCollapsed(group.id)}
              isAllSelected={isAllSelected(group)}
              handleAllChange={handleAllChange}
              handleIndividualChange={handleIndividualChange}
              hasDivider={group !== filterGroups[filterGroups.length - 1]}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
